import React from 'react';
import { Footer } from '../components/Footer';
import { useModal } from '../components/ModalContext';

// COPY DECISIONS (do not revert without checking with design)
//
// 1. Every quote on this page is real, said by a named person in a recorded
//    session. Do not write new quotes, do not tidy the phrasing, do not invent
//    attributions. If a quote needs to change, it gets replaced with another
//    real one.
// 2. /about is the only page that explains the Nisaba name. The home route
//    teases it in the ContextFlow aside and links here. Do not add the origin
//    story back to home.
// 3. Beliefs are prose, not bulleted pillars. Three icon cards of beliefs read
//    as a values wall. Prose reads like people talking.
// 4. The equity lines are drawn almost verbatim from Agasthya's ASU+GSV
//    language. They are deliberately modest: no scale claim, no outcome claim,
//    no numbers. Do not escalate them.
// 5. "Seamless" is banned Nisa language and shows up often in source material
//    pulled from personal sites and LinkedIn. Strip it whenever it appears.
//    Same for "ecosystem", "at scale", "amplify", and "dashboard".
//
// BIO RULES
//
// Each bio owns a different proof, so the four together read as range rather
// than as four versions of the same person:
//   Agasthya  has done the coaching job the product serves
//   Diego     has founded and shipped an education company of his own
//   Mariana   has put AI in front of teachers at products they already use
//   Greg      designs AI interfaces people trust, in edtech specifically
// Do not let two bios claim the same proof. If one changes, check the others.
//
// "AI" appears in three bios for three different reasons: Agasthya brings the
// coaching and data-science perspective, Mariana has shipped teacher-facing AI,
// and Greg owns interface trust. Keep those claims distinct.
//
// Bios are specific and short. Real places, real roles, one turn at the end.
// No abstract noun stacks like "educator, coach, researcher", which is how all
// four read originally and is indistinguishable from a job title. Fragments are
// fine. Keep every bio inside roughly 35 words so the four cards stay even at
// the four-up breakpoint. Every fact here is on a public profile.
//
// Do not use "carrying context forward" in Diego's bio. It appears in his own
// public writing and it is banned language in the Nisa guardrails.
//
// CONTRAST RULES (WCAG AA, matching the homepage pass)
//
// A. Small text on this page is gray-600 or darker, never gray-500.
// B. Focus rings are handled globally in index.css: ring-4, primary-600,
//    offset-2 on every button and anchor. Do not add per-element focus styles.
const beliefs = [{
  title: 'Good teaching grows with expert support',
  copy: 'Teaching expertise develops through practice and sustained support from another expert. We build Nisa around that ongoing work, helping coaches stay with a teacher’s growth over time.'
}, {
  title: 'Relationships drive improvement',
  copy: 'Coaching depends on trust and a coach who knows a teacher’s work well enough to help. We build Nisa with real coaches doing real work, learning from what those relationships need.'
}, {
  title: 'AI should support both',
  copy: 'We use AI to support teaching expertise and strengthen coaching relationships. Nisa helps coaches make sense of what they’ve gathered and prepare for the next conversation. Coaches decide how to carry the work forward.'
}];
const teamLeads = [{
  name: 'Agasthya Shenoy',
  role: 'Founder',
  bio: 'Former New Orleans math coach, Harvard research data scientist and AI Fellow at Teaching Lab. He builds technology to strengthen teacher expertise, human relationships and educational equity.'
}, {
  name: 'Diego Gonzalez',
  role: 'Engineering',
  bio: 'Founded THINKey, a STEAM learning company in Chile. He builds systems that hold complexity so people do not have to. Biochemistry and neurobiology before engineering.'
}, {
  name: 'Mariana Rego',
  role: 'Product',
  bio: 'Product at Nearpod, then BrainPOP, where she shipped its first AI features for teachers. She leads product and partnerships here, keeping the work grounded in who it serves.'
}, {
  name: 'Greg Wong',
  role: 'Design',
  bio: 'Designs AI interfaces people trust. 10+ years making hard work feel simpler across healthcare, fintech and education. Built n2y’s design function for classrooms with the most complex student needs.'
}];
export const AboutUs = () => {
  const {
    openTalkToUs
  } = useModal();
  return <div className="flex min-h-screen flex-col bg-white" data-id="element-397" data-mp="1jqozu1rztuli">
      <main className="relative flex-grow overflow-hidden" data-id="element-398" data-mp="15qe7svey69yr">
        <section className="w-full bg-white pb-14 pt-12 md:pb-20 md:pt-16" data-id="element-399" data-mp="1aopkrf0jh0l7">
          <div className="container mx-auto px-5 sm:px-8" data-id="element-400" data-mp="1aoz96y5xqzos">
            <div className="mx-auto max-w-6xl" data-id="element-401" data-mp="303dt9560dydy">
              <div className="grid items-center gap-10 md:grid-cols-12 md:gap-12" data-id="element-402" data-mp="301kro4a964ct">
                <div className="md:col-span-7" data-id="element-403" data-mp="351ywldgk4y6m">
                  <p className="mb-4 text-sm font-semibold uppercase tracking-[0.16em] text-primary-600" data-id="element-404" data-mp="3f65mukcwma59">
                    About Nisa
                  </p>
                  <h1 className="text-balance font-serif text-[34px] font-normal leading-[1.08] text-cyan-950 sm:text-[42px] lg:text-[46px]" data-id="element-405" data-mp="3a5hlb4r1uaws">
                    Educators are the authors of progress
                  </h1>
                  <p className="mt-6 max-w-2xl text-[17px] leading-relaxed text-gray-600 md:text-lg" data-id="element-406" data-mp="1rsk0402hkvb">
                    Nisa is built for instructional coaches and the educators
                    they work with. We help coaches build on what they know
                    about each teacher, supporting stronger teaching and better
                    conditions for students to learn.
                  </p>
                </div>

                <div className="flex justify-center md:col-span-5 md:justify-end" data-id="element-407" data-mp="2ckvb9kh281ia">
                  <img src="https://cdn.magicpatterns.com/uploads/exQgGpDcc2HgKsWSHKvs3M/image.png" alt="Abstract Nisa brand illustration representing growth and connected records" className="w-full max-w-[320px] object-contain sm:max-w-[360px] lg:max-w-[400px]" data-id="element-408" data-mp="28iqmp05gxv2i" />
                </div>
              </div>

              {/* Raised from gray-500 at 14px. The statewide RIDE work is the
                  strongest durability signal on the page and it was whispering. */}
              <div className="mt-9 grid max-w-4xl gap-x-10 gap-y-4 border-t border-gray-200 pt-6 sm:grid-cols-2" data-id="element-409" data-mp="qcilbjqnks8b">
                <p className="text-[15px] leading-relaxed text-gray-600" data-id="element-410" data-mp="zf9ypp339y2l">
                  Developed with Teaching Lab Studio, the innovation arm of
                  Teaching Lab.
                </p>
                <p className="text-[15px] leading-relaxed text-gray-600" data-id="element-411" data-mp="19534uakgpkzg">
                  Working statewide with the Rhode Island Department of
                  Education and with coaching teams at NYC Public Schools.
                </p>
              </div>

              <blockquote className="mt-14 max-w-4xl md:mt-16" data-id="element-412" data-mp="1i3ye5viszb6z">
                <p className="font-serif text-xl leading-relaxed text-cyan-950 md:text-2xl" data-id="element-413" data-mp="32p4sj4orhbtf">
                  “An educated society is one that recognizes the humanity in
                  all things, is curious about how and why things work, and is
                  empowered to make change. Our duty to the future is to
                  cultivate the conditions that make it possible.”
                </p>
                <footer className="mt-4 text-sm text-gray-600" data-id="element-414" data-mp="3marhtseczqh1">
                  Agasthya Shenoy, Founder
                </footer>
              </blockquote>
            </div>
          </div>
        </section>

        <section className="w-full bg-white pb-14 md:pb-20" data-id="element-415" data-mp="3ckopluf2agjr">
          <div className="container mx-auto px-5 sm:px-8" data-id="element-416" data-mp="37nofn8f3ctnd">
            <div className="mx-auto grid max-w-6xl gap-10 border-t border-gray-200 pt-12 lg:grid-cols-12 lg:gap-16 md:pt-16" data-id="element-417" data-mp="37lc35as3rbhp">
              <div className="lg:col-span-5" data-id="element-418" data-mp="46pram4maj33">
                <p className="mb-4 text-sm font-semibold uppercase tracking-[0.16em] text-primary-600" data-id="element-419" data-mp="44va9iwgvb4a">
                  Our name’s inspiration
                </p>
                <h2 className="text-balance font-serif text-h2-mobile font-normal leading-tight text-cyan-950 md:text-h2-desktop" data-id="element-420" data-mp="3df92zuzfgg18">
                  Keeper of the record
                </h2>
                <img src="https://cdn.magicpatterns.com/uploads/fEDZnoiKKvaKkPbnZQM2Q6/Nisaba.png" alt="Ancient relief depicting Nisaba, Sumerian goddess of writing and patron of scribes" loading="lazy" decoding="async" className="mt-7 w-32 object-contain drop-shadow-[0_8px_12px_rgba(15,65,74,0.16)] sm:w-36" style={{
                paddingLeft: "0px",
                paddingRight: "0px",
                marginLeft: "40px",
                marginRight: "40px",
                height: "160px"
              }} data-id="element-421" data-mp="33oo9un2iw3pp" />
              </div>
              <div className="lg:col-span-7" data-id="element-422" data-mp="26xc75vezuz3k">
                <p className="text-[17px] leading-relaxed text-gray-600 md:text-lg" data-id="element-423" data-mp="2by05l25jyi7t">
                  Our name comes from Nisaba, the Sumerian goddess of writing
                  and patron of scribes.
                </p>
                <p className="mt-5 text-[17px] leading-relaxed text-gray-600 md:text-lg" data-id="element-424" data-mp="1zg5xxf1cu9wy">
                  At the center of Nisa is a record of each teacher’s coaching
                  work. It keeps their goals connected to the conversations and
                  evidence behind them. It holds what came before so the next
                  conversation can start further along.
                </p>
              </div>
            </div>
          </div>
        </section>

        <section className="w-full border-y border-gray-200/70 bg-gray-50 py-14 md:py-20" data-id="element-425" data-mp="1ugb7fclhrmcf">
          <div className="container mx-auto px-5 sm:px-8" data-id="element-426" data-mp="1ui4cingghyib">
            <div className="mx-auto max-w-6xl" data-id="element-427" data-mp="1po0748mu9yjc">
              <div className="max-w-2xl" data-id="element-428" data-mp="11zqqdl4hg0z2">
                <p className="mb-4 text-sm font-semibold uppercase tracking-[0.16em] text-primary-600" data-id="element-429" data-mp="11ypcz9zzv2e6">
                  How we build
                </p>
                <h2 className="text-balance font-serif text-h2-mobile font-normal leading-tight text-cyan-950 md:text-h2-desktop" data-id="element-430" data-mp="1sk5mjg9qct58">
                  Three beliefs shaping Nisa
                </h2>
              </div>

              {/* Headings vary in length, so the three bodies would start at
                  different heights. The subgrid puts heading and body on shared
                  rows, min-h reserves two heading lines as a fallback where
                  subgrid is unavailable, and text-balance keeps a long heading
                  from leaving an orphan word. */}
              <div className="mt-10 grid items-start gap-9 border-t border-gray-200 pt-8 md:grid-cols-3 md:grid-rows-[auto_auto] md:gap-x-10 md:gap-y-3" data-id="element-431" data-mp="14t05ixs1zutc">
                {beliefs.map(({
                title,
                copy
              }) => <article key={title} className="md:row-span-2 md:grid md:grid-rows-subgrid" data-id="element-432" data-mp="pxu0oe4p221l">
                    <h3 className="text-balance font-serif text-xl font-normal leading-snug text-cyan-950 md:min-h-[3.5rem]" data-id="element-433" data-mp="lvgyfzp5om9k">
                      {title}
                    </h3>
                    <p className="mt-3 text-[16px] leading-relaxed text-gray-600 md:mt-0" data-id="element-434" data-mp="31knrdwsjwfl2">
                      {copy}
                    </p>
                  </article>)}
              </div>

              {/* Equity framing remains deliberately modest: no scale claim,
                  outcome claim or numbers. */}
              <p className="mt-10 max-w-3xl text-[17px] leading-relaxed text-gray-600" data-id="element-435" data-mp="2dvv0wfup6zxu">
                Every teacher, everywhere, deserves a dedicated partner in their growth.
              </p>
            </div>
          </div>
        </section>

        <section className="w-full bg-white py-14 md:py-20" data-id="element-436" data-mp="2ns7xpmljhtwt">
          <div className="container mx-auto px-5 sm:px-8" data-id="element-437" data-mp="2skitqed5svk3">
            <div className="mx-auto max-w-6xl" data-id="element-438" data-mp="2sm29m3uwl4lc">
              <div className="max-w-2xl" data-id="element-439" data-mp="1v3ttuq7rs3dc">
                <p className="mb-4 text-sm font-semibold uppercase tracking-[0.16em] text-primary-600" data-id="element-440" data-mp="1v30y0kacmy9z">
                  Who we are
                </p>
                <h2 className="text-balance font-serif text-h2-mobile font-normal leading-tight text-cyan-950 md:text-h2-desktop" data-id="element-441" data-mp="1s27atq0b8xk5">
                  Meet the lead builders of Nisa
                </h2>
                {/* The old intro listed the categories the bios now demonstrate
                    specifically, and counted the leads the grid already shows.
                    This keeps only what the cards cannot say: there is a wider
                    team, and educators correct us. */}
                <p className="mt-5 text-[17px] leading-relaxed text-gray-600" data-id="element-442" data-mp="153ujq9n3cucr">
                  A driven team with experience in classrooms and technology, shaped by the educators who tell us when we are wrong.
                </p>
              </div>

              <div className="mt-10 grid gap-x-10 gap-y-9 sm:grid-cols-2 lg:grid-cols-4" data-id="element-443" data-mp="1e5tzyzqa7uen">
                {teamLeads.map(lead => <article key={lead.name} className="border-t border-gray-200 pt-5" data-id="element-444" data-mp="2piwii0bxyu1g">
                    <p className="text-xs font-semibold uppercase tracking-[0.14em] text-primary-600" data-id="element-445" data-mp="bavbkptb7jq1">
                      {lead.role}
                    </p>
                    <h3 className="mt-2 font-serif text-xl font-normal text-cyan-950" data-id="element-446" data-mp="gbjd45f5ziyi">
                      {lead.name}
                    </h3>
                    <p className="mt-3 text-[15px] leading-relaxed text-gray-600" data-id="element-447" data-mp="790bfon47cdu">
                      {lead.bio}
                    </p>
                  </article>)}
              </div>

            </div>
          </div>
        </section>

        <section className="w-full bg-white pb-section-mobile md:pb-section-desktop" data-id="element-448" data-mp="yzemld3qualt">
          <div className="container mx-auto px-5 sm:px-8" data-id="element-449" data-mp="l9ulsctq3nf5">
            <div className="mx-auto max-w-6xl" data-id="element-450" data-mp="l78d29v3j1vc">
              <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-primary-50 via-white to-cyan-50/70 px-6 py-9 sm:px-10 md:py-11" data-id="element-451" data-mp="q74a1p5dlt1e">
                <div aria-hidden="true" className="pointer-events-none absolute -right-24 -top-28 h-72 w-72 rounded-full bg-cyan-200/30 blur-3xl" data-id="element-452" data-mp="1iogmorfzfc51" />
                <div className="relative max-w-2xl" data-id="element-453" data-mp="3q8tawo83xc3p">
                  <h2 className="text-balance font-serif text-2xl font-normal leading-snug text-cyan-950 md:text-3xl" data-id="element-454" data-mp="3l8x82sb5bqpq">
                    See Nisa in the work of coaching
                  </h2>
                  <p className="mt-4 text-[17px] leading-relaxed text-gray-600" data-id="element-455" data-mp="3c88rpqya5e9o">
                    Tell us how coaching works in your schools. We’ll walk
                    through how Nisa could support it.
                  </p>
                  <button type="button" onClick={openTalkToUs} className="mt-7 inline-flex min-h-12 items-center justify-center rounded-xl bg-primary-700 px-7 py-3 font-medium text-white shadow-sm transition-colors hover:bg-primary-800" data-id="element-456" data-mp="8ra5ba7d9ds1">
                    Request a demo
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer data-id="element-457" data-mp="rpcxfxv7nqu2" />
    </div>;
};